/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
class Cart extends Core {
	constructor() {
		super();
		this.digitalTypes = ["vouchers", "services"];
		this.cardProductType = "greeting card";

		this.subscriptionRules = ["letterbox", "bouquet", "drink", "chocolate", "vase", "plant"].reduce((acc, type) => {
			acc[type] = {
				qty: 0,
				cartMessage: "Subscriptions cannot be purchased with other items.",
				pdpMessage: "Your basket already contains a subscription. Remove it before adding other products.",
			};

			return acc;
		}, {});

		// If a productType has rules, then the rules define the limits (qty, messages, etc.).
		// If a productType is missing from the rules, then it is implicitly allowed.
		this.cartRules = [
			{
				productType: "bouquet",
				rules: {
					bouquet: {
						qty: 1,
						cartMessage: "You can only order one bouquet at a time",
						pdpMessage: "You already have a bouquet in your basket. Please empty it before adding a different bouquet.",
					},
					letterbox: {
						qty: 0,
						cartMessage: "Bouquet can't be sent with letterbox",
						pdpMessage: "You already have a bouquet in your basket. Please empty it before adding a different bouquet.",
					},
					plant: {
						qty: 0,
						cartMessage: "No plants allowed with bouquet",
						pdpMessage: "You already have a bouquet in your basket. Please empty it before adding a plant.",
					},
					vase: {
						qty: 1,
						cartMessage: "Max 1 vase with a bouquet",
						pdpMessage: "You already have a vase in your basket. Please empty it before adding a new one.",
					},
				},
			},
			{
				productType: "letterbox",
				rules: {
					letterbox: {
						qty: 1,
						cartMessage: "Letterbox must be sent alone",
						pdpMessage: "You already have a bouquet in your basket. Please empty it before adding a different bouquet.",
					},
					bouquet: {
						qty: 0,
						cartMessage: "Letterbox must be sent alone",
						pdpMessage: "You already have a bouquet in your basket. Please empty it before adding a different bouquet.",
					},
					drink: {
						qty: 0,
						cartMessage: "No drinks allowed with letterbox",
						pdpMessage: "You cannot send other items with a letterbox bouquet. ",
					},
					chocolate: {
						qty: 0,
						cartMessage: "Max 1 chocolate with letterbox",
						pdpMessage: "You cannot send other items with a letterbox bouquet. ",
					},
					vase: {
						qty: 0,
						cartMessage: "No vases allowed with letterbox",
						pdpMessage: "You cannot send other items with a letterbox bouquet. ",
					},
					plant: {
						qty: 0,
						cartMessage: "No plants allowed with letterbox",
						pdpMessage: "You cannot send other items with a letterbox bouquet. ",
					},
				},
			},
			{
				productType: "personal subscription",
				rules: this.subscriptionRules,
			},
			{
				productType: "gift subscription",
				rules: this.subscriptionRules,
			},
		];

		this.container = document.getElementById("cart-drawer");
		this.contents = this.container.querySelector("#cart-drawer-contents");
		this.itemsWrapper = this.container.querySelector("#cart-drawer-items");
		this.itemTemplate = document.querySelector("#cart-drawer-item-template");
		this.groupTemplate = document.querySelector("#cart-drawer-group-template");
		this.errorContainer = this.container.querySelector("#cart-error-messages");

		this.discountContainer = document.querySelector("#discount-wrapper");
		this.showDiscountFormBtn = this.discountContainer.querySelector(".discount-opener");
		this.discountForm = this.discountContainer.querySelector(".discount-form");

		this.thresholdNotReached = this.container.querySelector("[data-cart-threshold-not-reached]");
		this.thresholdPrompt = this.container.querySelector("[data-cart-threshold-prompt]");

		this.checkoutBtn = this.container.querySelector("[data-cart-checkout]");

		this.empty = this.container.querySelector("#cart-drawer-empty");
		this.actions = this.container.querySelector("#cart-drawer-actions");
		this.cartDrawerControllers = Array.from(document.querySelectorAll(`[aria-controls=${this.container.id}]`));
		this.overlay = document.getElementById("cart-overlay");

		this.isExpanded = false;

		this.progressContainer = this.container.querySelector("#progress-container");
		this.progressBar = this.progressContainer.querySelector(".progress");
		this.progressMessage = this.progressContainer.querySelector(".message");

		this.giftBuilder = new GiftBuilder();

		// Add cart functions to global window
		window.arena = window.arena || {};
		Object.assign(window.arena, {
			toggleCartDrawer: this.toggleCartDrawer.bind(this),
			showErrorMessage: this.showErrorMessage.bind(this),
			getProductTypeCounts: this.getProductTypeCounts.bind(this),
			isValidCart: this.isValidCart.bind(this),
			checkMinOrderValue: this.checkMinOrderValue.bind(this),
		});
	}

	showErrorMessage(title, message, openCart = false) {
		const titleEl = this.errorContainer.querySelector(".message-title");
		const bodyEl = this.errorContainer.querySelector(".message-body");

		if (!title && !message) {
			this.errorContainer.removeAttribute("data-open");

			titleEl.innerText = "";
			bodyEl.innerText = "";
			return;
		}

		if (!openCart && !this.showErrors) return;

		titleEl.innerText = title || "";
		bodyEl.innerHTML = message || "";

		const padding = this.errorContainer.hasAttribute("data-open") ? 0 : 16;
		const elementHeight = this.errorContainer.scrollHeight + padding;
		this.errorContainer.style.setProperty("--messageHeight", `${elementHeight}px`);

		let messageDelay = 0;
		if (openCart && !this.isExpanded) {
			this.errorContainer.removeAttribute("data-open");
			this.toggleCartDrawer();
			messageDelay = 1000;
		}

		setTimeout(() => {
			// Delay message until cart is fully opened.
			this.errorContainer.setAttribute("data-open", "data-open");
		}, messageDelay);
	}

	checkCartStatus() {
		if (!this.minimumOrderValueAchieved) {
			// It will show keep browsing button and disable checkout if min order wasn't reached
			this.checkoutBtn.setAttribute("aria-disabled", "true");
			this.thresholdNotReached.toggleAttribute("hidden", false);
			return false;
		}

		const isValidCart = this.isValidCart(this.getProductTypeCounts());
		if (!isValidCart.valid) {
			this.checkoutBtn.setAttribute("aria-disabled", "true");
			if (this.showErrors) {
				const errors = isValidCart.reasons
					.map((r) => (r.cartMessage ? `<li>${r.cartMessage}</li>` : false))
					.filter(Boolean);
				if (errors.length > 0)
					this.showErrorMessage("Please review your cart", `<ul class="list-disc list-inside">${errors.join("")}</ul>`);
			}
			return false;
		}

		if (this.showErrors) this.showErrorMessage();
		this.checkoutBtn.removeAttribute("aria-disabled");
		this.thresholdNotReached.toggleAttribute("hidden", true);

		return true;
	}

	checkMinOrderValue() {
		return this.minimumOrderValueAchieved;
	}

	getProductTypeCounts() {
		if (!window.cart.cartObject || !window.cart.cartObject.lines) return {};

		return window.cart.cartObject.lines.nodes.reduce((acc, item) => {
			const type = item.merchandise.product.productType.toLowerCase();
			acc[type] = (acc[type] || 0) + item.quantity;
			return acc;
		}, {});
	}

	isValidCart(productTypeCounts) {
		const productTypesSum = productTypeCounts || this.getProductTypeCounts();
		const issues = [];

		if (productTypesSum[this.cardProductType] && productTypesSum[this.cardProductType] > 1) {
			issues.push({
				activeRule: this.cardProductType,
				reason: "Greeting Card",
				cartMessage: "Only one greeting card is allowed per checkout.",
				pdpMessage: "Only one greeting card is allowed per checkout.",
			});
		}

		const activeRules = this.cartRules.filter(
			(rule) => productTypesSum[rule.productType.toLowerCase()] && productTypesSum[rule.productType.toLowerCase()] > 0
		);

		activeRules.forEach((activeRule) => {
			Object.entries(productTypesSum).forEach(([type, qty]) => {
				const currentItemRules = activeRule.rules[type.toLowerCase()];
				if (currentItemRules && currentItemRules.qty < qty) {
					issues.push({
						activeRule: activeRule.productType,
						reason: type,
						cartMessage: currentItemRules.cartMessage,
						pdpMessage: currentItemRules.pdpMessage,
					});
				}
			});
		});

		return issues.length === 0 ? { valid: true } : { valid: false, reasons: issues };
	}

	toggleCartDrawer(showErrors = false) {
		this.showErrors = showErrors;

		this.isExpanded = this.container.getAttribute("aria-expanded") === "true";
		this.container.setAttribute("aria-expanded", `${!this.isExpanded}`);
		document.documentElement.classList.toggle("overflow-hidden", !this.isExpanded);
		this.isExpanded = !this.isExpanded;

		if (this.isExpanded) this.updateCartContents();
	}

	get minimumOrderValueAchieved() {
		if (!this.minimumOrderValue) return true; // it can be optional false doesn't check

		const cart = window?.cart?.cartObject || false;
		if (!cart) return false;

		return this.getCartType() === "digital" ? true : this.getPhysicalSubtotal() >= this.minimumOrderValue;
	}

	updateProgressBar() {
		const cartType = this.getCartType();
		const cartSubtotal = this.getPhysicalSubtotal();
		const minOrder = this.minimumOrderValue;
		const freeShipping = this.freeDeliveryThreshold;
		const missingMinValue = minOrder - cartSubtotal;
		const missingFreeShipping = freeShipping ? freeShipping - cartSubtotal : 0;

		if (cartType === "digital" || (missingMinValue <= 0 && missingFreeShipping <= 0)) {
			this.progressContainer.classList.add("hidden");
			return;
		}

		const getProgressMessage = () => {
			if (missingMinValue > 0) {
				return cartType === "mixed"
					? `Add £${missingMinValue.toFixed(2)} to reach our minimum spend on deliverable items`
					: `Add £${missingMinValue.toFixed(2)} to reach our minimum order of £${minOrder}`;
			}

			if (missingFreeShipping > 0) {
				return cartType === "mixed"
					? `Add £${missingFreeShipping.toFixed(2)} to get free standard delivery on deliverable items`
					: `Add £${missingFreeShipping.toFixed(2)} to get free standard delivery`;
			}

			return "";
		};

		const getProgressWidth = (x, min, max) => ((x - min) / (max - min)) * 100;

		let progressWidth;

		if (missingMinValue > 0) {
			progressWidth = getProgressWidth(cartSubtotal, 0, minOrder);
		} else if (missingFreeShipping > 0) {
			progressWidth = getProgressWidth(cartSubtotal, 0, freeShipping);
		} else {
			progressWidth = 100;
		}

		this.progressContainer.classList.remove("hidden");
		this.progressBar.style.width = `${progressWidth}%`;
		this.progressMessage.innerText = getProgressMessage();
	}

	updateCartContents() {
		const cartSize = window?.cart?.cartSize || 0;
		this.contents.classList.toggle("hidden", cartSize === 0);
		this.empty.classList.toggle("hidden", cartSize !== 0);
		this.actions.classList.toggle("hidden", cartSize === 0);

		this.showDiscountFormBtn.classList.remove("hidden");
		this.discountForm.classList.add("hidden");
		this.itemsWrapper.innerHTML = "";

		if (window.cart.cartObject && window.cart.cartObject.lines.nodes) {
			const cartItems = this.getBundleItems(window.cart.cartObject.lines.nodes);
			cartItems.forEach(this.renderCartItem.bind(this));
		}

		this.updateSubtotal();
		this.updateTotal();
		this.updateProgressBar();
		this.checkCartStatus();
	}

	updateSubtotal() {
		const deliveryPriceEle = this.container.querySelector("[data-cart-delivery-price]");
		const subtotalEle = this.container.querySelector("[data-cart-subtotal]");
		subtotalEle.innerText = Core.formatCurrency(window.cart.subtotal);

		// Does not show delivery fee for cart with only vouchers
		if (this.getCartType() === "digital") {
			deliveryPriceEle.parentElement.classList.add("hidden");
			this.deliveryPrice = 0;
			return;
		}

		deliveryPriceEle.parentElement.classList.remove("hidden");

		// Once Shopify has resolved a real delivery option (e.g. after visiting checkout), its cost is
		// already included in window.cart.total - display it, but don't add it again in updateTotal().
		const deliveryGroups = window.cart.cartObject?.deliveryGroups?.nodes || [];
		const resolvedDeliveryGroups = deliveryGroups.filter((group) => group.selectedDeliveryOption);
		const resolvedDeliveryPrice = resolvedDeliveryGroups.reduce(
			(total, group) => total + parseFloat(group.selectedDeliveryOption.estimatedCost.amount),
			0
		);

		// Groups without a selected option yet aren't reflected in window.cart.total, so they still
		// need the flat estimate - otherwise a partially-resolved cart understates its total.
		const allGroupsResolved = deliveryGroups.length > 0 && resolvedDeliveryGroups.length === deliveryGroups.length;
		if (allGroupsResolved) {
			this.deliveryPrice = 0;
		} else if (this.freeDeliveryThreshold) {
			this.deliveryPrice = this.getPhysicalSubtotal() >= this.freeDeliveryThreshold ? 0 : this.minDeliveryCost;
		} else {
			this.deliveryPrice = this.minDeliveryCost;
		}

		const displayPrice = resolvedDeliveryPrice + this.deliveryPrice;
		deliveryPriceEle.innerText = displayPrice > 0 ? Core.formatCurrency(displayPrice) : "FREE";
	}

	/**
	 * Verify if cart contains digital items
	 * @returns "mixed" | "digital" | "physical"
	 */
	getCartType() {
		// returns true if cart mixes deliverable and no deliverable items
		const itemTypes = Array.from(
			new Set(window.cart.cartObject?.lines.nodes.map((item) => item.merchandise.product.productType) || [])
		);

		const hasDigitalItems = itemTypes.some((item) => this.digitalTypes.includes(item));
		const otherItems = itemTypes.filter((item) => !this.digitalTypes.includes(item));

		if (hasDigitalItems) return otherItems.length > 0 ? "mixed" : "digital";
		return "physical";
	}

	/**
	 * Obtain cart subtotal excluding digital items
	 * @returns float
	 */
	getPhysicalSubtotal() {
		return (
			window.cart.cartObject?.lines.nodes.reduce((acc, item) => {
				const { productType } = item.merchandise.product;
				if (this.digitalTypes.includes(productType)) return acc;
				return acc + parseFloat(item.merchandise.price.amount) * item.quantity;
			}, 0) || 0
		);
	}

	updateTotal() {
		const cart = window?.cart?.cartObject || false;
		if (!cart) return;

		const totalEle = this.container.querySelector("[data-cart-total]");
		const discountEle = this.container.querySelector("[data-cart-discounts]");

		const { discountAllocations, appliedGiftCards } = window?.cart?.cartObject || {};

		discountEle.classList.add("hidden");
		discountEle.innerHTML = "Discounts";

		const renderDiscountItem = (discount) => {
			const disValue = discount.discountedAmount || discount.amountUsed;
			const disCode = discount.code || discount.lastCharacters || "DISCOUNT";
			const codeRepeated = discountEle.querySelector(`[data-discount-code = '${disCode.toUpperCase()}']`);
			if (codeRepeated) {
				const appliedAmount = codeRepeated.getAttribute("data-discount-amount");
				const totalAmount = parseFloat(appliedAmount, 10) + parseFloat(disValue.amount, 10);
				codeRepeated.setAttribute("data-discount-amount", totalAmount);
				codeRepeated.innerText = `- ${Core.formatCurrency(totalAmount)}`;
			} else {
				const discountItemsWrapper = document.createElement("div");
				const discountItem = document.createElement("div");
				const discountCode = document.createElement("div");
				const discountAmount = document.createElement("div");
				discountItemsWrapper.classList.add("flex", "flex-col");
				discountItem.classList.add("flex", "justify-between");
				discountCode.classList.add("opacity-50", "uppercase", "text-xs");
				discountAmount.setAttribute("data-discount-code", disCode.toUpperCase());
				discountAmount.setAttribute("data-discount-amount", disValue.amount);

				discountCode.innerText = discount.lastCharacters ? `...${disCode.toUpperCase()}` : disCode.toUpperCase();
				discountAmount.innerText = `- ${Core.formatCurrency(disValue.amount)}`;

				discountItem.append(discountCode, discountAmount);
				discountItemsWrapper.appendChild(discountItem);
				discountEle.appendChild(discountItemsWrapper);
			}
		};

		discountAllocations.forEach(renderDiscountItem);
		appliedGiftCards.forEach(renderDiscountItem);

		if (discountAllocations.length || appliedGiftCards.length) discountEle.classList.remove("hidden");

		totalEle.innerText = Core.formatCurrency(window.cart.total + this.deliveryPrice);
	}

	renderCartItem(cartItem) {
		const { mainItem } = cartItem;

		const item = mainItem ? this.buildCartGroupItem(cartItem) : this.buildCartItem(cartItem);

		this.itemsWrapper.appendChild(item);
	}

	buildCartItem(cartItem) {
		const { merchandise, quantity, discountAllocations } = cartItem;
		const { image, product, title, price, availableForSale } = merchandise;
		const compareAtTotal = parseFloat(merchandise.compareAtPrice?.amount || 0) * quantity;
		const priceTotal = parseFloat(price.amount) * quantity;

		const cartItemNode = this.itemTemplate.content.cloneNode(true);
		const cartItemBox = cartItemNode.querySelector("div");
		const imageEle = cartItemNode.querySelector("[data-cart-item-image]");
		imageEle.setAttribute("src", image.url);
		imageEle.setAttribute("alt", product.title);

		const titleEle = cartItemNode.querySelector("[data-cart-item-title]");
		titleEle.innerText = product.title;
		titleEle.setAttribute("href", `/products/${product.handle}/`);

		const variantTitleEle = cartItemNode.querySelector("[data-cart-item-variant-title]");
		if (title !== "Default Title") variantTitleEle.innerText = title;

		const priceEle = cartItemNode.querySelector("[data-cart-item-price]");
		priceEle.innerText = Core.formatCurrency(priceTotal);

		if (compareAtTotal > priceTotal) {
			const comparePriceEle = cartItemNode.querySelector("[data-cart-compare-at-price]");
			comparePriceEle.innerText = Core.formatCurrency(compareAtTotal);
		}

		const discounts = discountAllocations.reduce(
			(a, d) => ({ total: parseFloat(d.discountedAmount.amount) + a.total, codes: [...a.codes, d.code] }),
			{ total: 0, codes: [] }
		);

		if (discounts.total > 0) {
			// Show discounted group final price and add line-through to previous price
			const discountPriceEle = document.createElement("p");
			discountPriceEle.classList.add("font-bold", "text-black", "text-center");
			discountPriceEle.innerText = Core.formatCurrency(priceTotal - discounts.total);

			if (compareAtTotal > 0) {
				// Check if there is compareAt price to avoid double line-through prices
				priceEle.classList.add("hidden");
			} else {
				priceEle.classList.add("line-through", "opacity-50");
			}

			priceEle.parentElement.appendChild(discountPriceEle);

			// Show applicable codes
			const codeListEl = cartItemNode.querySelector("[data-cart-discount-codes]");
			codeListEl.classList.remove("hidden");

			const codeListItemEl = codeListEl.querySelector("li");

			discounts.codes.forEach((code) => {
				const listItemEl = codeListItemEl.cloneNode();
				listItemEl.innerText = code.toUpperCase();
				listItemEl.classList.remove("hidden");
				codeListEl.appendChild(listItemEl);
			});
		}

		if (!availableForSale) {
			const soldOutEl = cartItemNode.querySelector("[data-cart-item-soldout]");
			priceEle.classList.add("opacity-50");
			titleEle.classList.add("opacity-50");
			imageEle.classList.add("opacity-50");
			soldOutEl.classList.remove("hidden");
		}

		const removeItemEle = cartItemNode.querySelector("[data-cart-item-remove]");
		removeItemEle.addEventListener("click", () => this.handleRemoveItem(cartItem, cartItemBox));

		return cartItemNode;
	}

	buildCartGroupItem(cartGroup) {
		const { mainItem, items } = cartGroup;
		const { merchandise, quantity, discountAllocations } = mainItem;
		const { image, product, title, price, availableForSale } = merchandise;
		const mainItemCompareAt = parseFloat(merchandise.compareAtPrice?.amount || 0) * quantity;

		let groupPriceTotal = parseFloat(price.amount) * quantity;
		let groupDiffCompareAtTotal = mainItemCompareAt > groupPriceTotal ? mainItemCompareAt - groupPriceTotal : 0;
		let groupDiscountAlloc = discountAllocations.reduce(
			(a, d) => ({ total: parseFloat(d.discountedAmount.amount) + a.total, codes: [...a.codes, d.code] }),
			{ total: 0, codes: [] }
		);

		const cartItemNode = this.groupTemplate.content.cloneNode(true);
		const cartItemBox = cartItemNode.querySelector("div");
		const imageEle = cartItemNode.querySelector("[data-cart-item-image]");
		imageEle.setAttribute("src", image.url);
		imageEle.setAttribute("alt", product.title);

		const titleEle = cartItemNode.querySelector("[data-cart-item-title]");
		titleEle.innerText = product.title;
		titleEle.setAttribute("href", `/products/${product.handle}/`);

		const variantTitleEle = cartItemNode.querySelector("[data-cart-item-variant-title]");
		if (title !== "Default Title") variantTitleEle.innerText = title;

		// Lists group items
		const groupItemsList = cartItemNode.querySelector("[data-cart-group-items]");
		items.forEach((item) => {
			const { merchandise: groupItemMerch, quantity: groupItemQty, discountAllocations: groupDiscountAloc } = item;
			const itemPrice = parseFloat(groupItemMerch.price.amount) * groupItemQty;
			const itemCompareAt = parseFloat(groupItemMerch.compareAtPrice?.amount || 0) * groupItemQty;

			groupDiscountAlloc = groupDiscountAloc.reduce(
				(a, d) => ({
					total: parseFloat(d.discountedAmount.amount) + a.total,
					codes: a.codes.includes(d.code) ? a.codes : [...a.codes, d.code],
				}),
				groupDiscountAlloc
			);

			const groupitemEl = document.createElement("li");
			groupitemEl.classList.add("pl-2");
			groupitemEl.innerText = groupItemMerch.product.title;
			groupItemsList.appendChild(groupitemEl);

			groupPriceTotal += itemPrice;
			if (itemCompareAt > itemPrice) {
				groupDiffCompareAtTotal += itemCompareAt - itemPrice;
			}
		});

		const priceEle = cartItemNode.querySelector("[data-cart-item-price]");
		priceEle.innerText = Core.formatCurrency(groupPriceTotal);

		if (groupDiffCompareAtTotal > 0) {
			const comparePriceEle = cartItemNode.querySelector("[data-cart-compare-at-price]");
			comparePriceEle.innerText = Core.formatCurrency(groupDiffCompareAtTotal + groupPriceTotal);
		}

		if (groupDiscountAlloc.total > 0) {
			// Show discounted group final price and add line-through to previous price
			const discountPriceEle = document.createElement("p");
			discountPriceEle.classList.add("font-bold", "text-black", "text-center");
			discountPriceEle.innerText = Core.formatCurrency(groupPriceTotal - groupDiscountAlloc.total);

			if (groupDiffCompareAtTotal > 0) {
				// Check if there is compareAt price to avoid double line-through prices
				priceEle.classList.add("hidden");
			} else {
				priceEle.classList.add("line-through", "opacity-50");
			}

			priceEle.parentElement.appendChild(discountPriceEle);

			// Show applicable codes
			const codeListEl = cartItemNode.querySelector("[data-cart-discount-codes]");
			codeListEl.classList.remove("hidden");

			const codeListItemEl = codeListEl.querySelector("li");

			groupDiscountAlloc.codes.forEach((code) => {
				const listItemEl = codeListItemEl.cloneNode();
				listItemEl.innerText = code.toUpperCase();
				listItemEl.classList.remove("hidden");
				codeListEl.appendChild(listItemEl);
			});
		}

		if (!availableForSale) {
			const soldOutEl = cartItemNode.querySelector("[data-cart-item-soldout]");
			priceEle.classList.add("opacity-50");
			titleEle.classList.add("opacity-50");
			imageEle.classList.add("opacity-50");
			soldOutEl.classList.remove("hidden");
		}

		const removeItemEle = cartItemNode.querySelector("[data-cart-item-remove]");
		removeItemEle.addEventListener("click", () => this.handleRemoveItem(cartGroup, cartItemBox));

		const editItemEle = cartItemNode.querySelector("[data-cart-item-edit]");
		editItemEle.addEventListener("click", () => this.handleEditGroup(cartGroup, cartItemBox));

		return cartItemNode;
	}

	async handleAddDiscountCode() {
		const discountError = this.discountContainer.querySelector(".discount-error");
		const input = this.discountContainer.querySelector(".discount-form input");
		const code = input.value;
		discountError.classList.add("hidden");

		const result = await super.addDiscountCode(code);

		const isGiftCode = code.split(" ").length > 3;

		if (isGiftCode) {
			if (!result.appliedGiftCards.find((dc) => dc.lastCharacters.toLowerCase() === code.slice(-4).toLowerCase())) {
				discountError.classList.remove("hidden");
				return;
			}
		} else if (!result.discountCodes.find((dc) => dc.code.toLowerCase() === code.toLowerCase() && dc.applicable)) {
			discountError.classList.remove("hidden");
			return;
		}

		this.updateCartContents();
	}

	handleDiscountForm() {
		const input = this.discountForm.querySelector("#voucher-code");

		this.showDiscountFormBtn.addEventListener("click", () => {
			this.showDiscountFormBtn.classList.add("hidden");
			this.discountForm.classList.remove("hidden");
			input.focus();
		});

		this.discountForm.addEventListener("submit", (event) => {
			event.preventDefault();
			this.handleAddDiscountCode();
		});
	}

	handleCartDrawerControllers() {
		this.cartDrawerControllers.forEach((controller) =>
			controller.addEventListener("click", this.toggleCartDrawer.bind(this, true))
		);
		this.overlay.addEventListener("click", this.toggleCartDrawer.bind(this));
	}

	getBundleItems(cartItems) {
		// Quick lookup for items by merchandise.id
		const byId = Object.fromEntries(cartItems.map((item) => [item.merchandise.id, item]));
		// It is needed to make sure the budle main item wasn't removed
		const mainItems = cartItems
			.filter((item) => !item.attributes?.some((a) => a.key === "_main_item"))
			.map((item) => item.merchandise.id);

		const groups = {};
		const groupedItems = new Set();

		// Step 1: build groups based on `_main_item`
		cartItems.forEach((item) => {
			const mainAttr = item.attributes.find((a) => a.key === "_main_item");
			if (mainAttr && mainItems.includes(mainAttr.value)) {
				const mainId = mainAttr.value;

				// Create group if it doesn't exist
				if (!groups[mainId]) {
					groups[mainId] = {
						mainItem: byId[mainId] || { merchandise: { id: mainId }, attributes: [] },
						items: [],
					};

					if (byId[mainId]) {
						// add the main item as a member os grouped items (to filter out as orphan later)
						groupedItems.add(byId[mainId]);
					}
				}

				groups[mainId].items.push(item); // add this item as an item in the bundle
				groupedItems.add(item); // mark this item as a grouped item (to filter out as orphan later)
			}
		});

		// Step 2: combine groups + orphan items
		return [...Object.values(groups), ...cartItems.filter((item) => !groupedItems.has(item))];
	}

	// Cart item update methods

	async handleRemoveItem(item, node) {
		if (node) node.classList.add("opacity-50");

		// it can be a single item(id) or a group(mainItem , items)
		const { mainItem, items, id } = item;
		const lineItemIds = [];

		if (id) lineItemIds.push(id);
		if (mainItem) lineItemIds.push(mainItem.id);
		if (items) lineItemIds.push(...items.map((i) => i.id));

		await this.removeItemsCart({
			cartId: window.cart.cartId,
			site: this.siteId,
			lineItemIds,
		});

		this.showErrors = true;

		if (node) this.updateCartContents();
	}

	async handleEditGroup(group, node) {
		const { mainItem, items } = group;
		const storedSteps = this.giftBuilder.loadStoredSteps(mainItem.merchandise.id);

		// TODO:
		// If don;t find steps show a message TBD

		const giftBuilderOptions = {
			mainItem: {
				merchandiseId: mainItem.merchandise.id,
				price: parseFloat(mainItem.merchandise.price.amount),
			},
			initialPayload: items.map((item) => ({
				merchandiseId: item.merchandise.id,
				quantity: 1,
				price: parseFloat(item.merchandise.price.amount),
				attributes: item.attributes,
			})),
			thumbnail: mainItem.merchandise.image.url,
			abortBtnText: "Close",
			saveBtnText: "Save",
			steps: storedSteps,
		};

		node.classList.add("opacity-50");

		this.giftBuilder.start(giftBuilderOptions, async (response) => {
			if (response) {
				// Can't use updateItems API because it might need to add new items
				await this.handleRemoveItem(group);
				await window.arena.addItemsCart({
					site: this.siteId,
					...(window.cart.cartId && { cartId: window.cart.cartId }),
					items: [
						{
							merchandiseId: mainItem.merchandise.id,
							quantity: 1,
						},
						...response,
					],
				});
			}

			this.showErrors = true;
			this.updateCartContents();
		});
	}

	showErrorToltip(e) {
		// Show a tooltip hovering over the checkout button when trying to checkout with an invalid cart
		const promptVisible = this.thresholdPrompt.getAttribute("hidden") === null;
		if (promptVisible) {
			this.thresholdPrompt.classList.add("animate-shake");
			this.thresholdPrompt.addEventListener(
				"animationend",
				() => {
					this.thresholdPrompt.classList.remove("animate-shake");
				},
				{ once: true }
			);
		} else {
			this.thresholdPrompt.removeAttribute("hidden");
			this.thresholdPrompt.classList.add("animate-fade-in");
			this.thresholdPrompt.addEventListener(
				"animationend",
				() => {
					this.thresholdPrompt.classList.remove("animate-fade-in");
				},
				{ once: true }
			);
		}
		e.stopImmediatePropagation();
		window.addEventListener(
			"click",
			() => {
				this.thresholdPrompt.classList.add("animate-fade-out");
				this.thresholdPrompt.addEventListener(
					"animationend",
					() => {
						this.thresholdPrompt.setAttribute("hidden", "hidden");
						this.thresholdPrompt.classList.remove("animate-fade-out");
					},
					{ once: true }
				);
			},
			{ once: true }
		);
	}

	handleGoToCheckout() {
		this.checkoutBtn.addEventListener("click", (e) => {
			const isValidCart = this.checkCartStatus(true);
			if (!isValidCart || !this.minimumOrderValueAchieved) {
				this.showErrorToltip(e);
			} else {
				// &skip_shop_pay=true
				// It's an unofficial solution to avoid the default use of shop pay at checkout,
				// not supported or announced by shopify, so there is no guarantee that it will always work.
				window.location.href = `${window.cart.cartObject.checkoutUrl}&skip_shop_pay=true`;
			}
		});
	}

	init() {
		this.handleCartDrawerControllers();
		this.handleGoToCheckout();
		this.handleDiscountForm();

		const params = new URL(document.location).searchParams;
		if (params.size !== 0 && typeof params.get("cartOpen") === "string") {
			setTimeout(this.toggleCartDrawer.bind(this, true), 2400);
		}
	}
}

// eslint-disable-next-line no-new
const cart = new Cart();
cart.init();
